import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { checklistTemplates, checklistExecutions, areas } from '../data/mockData';
import { toast } from 'sonner';

const ChecklistContext = createContext(null);

// Load from localStorage or use seed data
const loadTemplates = () => {
  try {
    const stored = localStorage.getItem('smcp_checklist_templates');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.warn('Failed to load templates from localStorage:', e);
  }
  return checklistTemplates.map(t => ({
    ...t,
    questions: t.questions || [],
    questionsList: [] // Will be populated with actual question objects
  }));
};

const loadExecutions = () => {
  try {
    const stored = localStorage.getItem('smcp_checklist_executions');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.warn('Failed to load executions from localStorage:', e);
  }
  return checklistExecutions;
};

// Generate next ID
const getNextTemplateId = (templates) => {
  const numbers = templates
    .map(t => {
      const match = t.id.match(/CL-(\d+)/);
      return match ? parseInt(match[1], 10) : 0;
    })
    .filter(n => !isNaN(n));
  const maxNum = numbers.length > 0 ? Math.max(...numbers) : 0;
  return `CL-${String(maxNum + 1).padStart(3, '0')}`;
};

const getNextExecutionId = (executions) => {
  const numbers = executions
    .map(e => {
      const match = e.id.match(/EXEC-(\d+)/);
      return match ? parseInt(match[1], 10) : 0;
    })
    .filter(n => !isNaN(n));
  const maxNum = numbers.length > 0 ? Math.max(...numbers) : 0;
  return `EXEC-${String(maxNum + 1).padStart(3, '0')}`;
};

export const ChecklistProvider = ({ children }) => {
  const [templates, setTemplates] = useState(loadTemplates);
  const [executions, setExecutions] = useState(loadExecutions);

  // Persist to localStorage whenever templates or executions change
  useEffect(() => {
    try {
      localStorage.setItem('smcp_checklist_templates', JSON.stringify(templates));
    } catch (e) {
      console.warn('Failed to save templates to localStorage:', e);
    }
  }, [templates]);

  useEffect(() => {
    try {
      localStorage.setItem('smcp_checklist_executions', JSON.stringify(executions));
    } catch (e) {
      console.warn('Failed to save executions to localStorage:', e);
    }
  }, [executions]);

  // Template CRUD operations
  const createTemplate = useCallback((payload) => {
    const newTemplate = {
      id: getNextTemplateId(templates),
      name: payload.name,
      version: '1.0',
      status: 'Draft',
      effectiveDate: null,
      nextReview: null,
      questions: 0,
      category: payload.category,
      appliesToArea: payload.appliesToArea || 'all',
      frequency: payload.frequency,
      defaultOwnerRole: payload.defaultOwnerRole,
      criticality: payload.criticality,
      linkedReqs: [],
      questionsList: []
    };
    setTemplates(prev => [...prev, newTemplate]);
    toast.success('Template created', { description: `Created ${newTemplate.id}` });
    return newTemplate;
  }, [templates]);

  const updateTemplate = useCallback((id, patch) => {
    setTemplates(prev => prev.map(t => 
      t.id === id ? { ...t, ...patch } : t
    ));
    toast.success('Template updated');
  }, []);

  const publishTemplate = useCallback((id) => {
    setTemplates(prev => prev.map(t => {
      if (t.id !== id) return t;
      const isRepublish = t.status === 'Effective';
      const versionParts = t.version.split('.');
      const newVersion = isRepublish 
        ? `${versionParts[0]}.${parseInt(versionParts[1] || 0) + 1}`
        : t.version;
      return {
        ...t,
        status: 'Effective',
        version: newVersion,
        effectiveDate: new Date().toISOString().split('T')[0]
      };
    }));
    toast.success('Template published', { description: 'Template is now effective' });
  }, []);

  const duplicateTemplate = useCallback((id) => {
    const template = templates.find(t => t.id === id);
    if (!template) return null;
    const newTemplate = {
      ...template,
      id: getNextTemplateId(templates),
      name: `${template.name} (Copy)`,
      version: '1.0',
      status: 'Draft',
      effectiveDate: null,
      questionsList: template.questionsList ? [...template.questionsList] : []
    };
    setTemplates(prev => [...prev, newTemplate]);
    toast.success('Template duplicated', { description: `Created ${newTemplate.id}` });
    return newTemplate;
  }, [templates]);

  const retireTemplate = useCallback((id) => {
    setTemplates(prev => prev.map(t => 
      t.id === id ? { ...t, status: 'Retired' } : t
    ));
    toast.success('Template retired');
  }, []);

  // Question operations
  const addQuestion = useCallback((templateId, question) => {
    const questionId = `Q${Date.now()}`;
    const newQuestion = {
      id: questionId,
      ...question
    };
    setTemplates(prev => prev.map(t => {
      if (t.id !== templateId) return t;
      const newQuestionsList = [...(t.questionsList || []), newQuestion];
      return {
        ...t,
        questionsList: newQuestionsList,
        questions: newQuestionsList.length
      };
    }));
    return newQuestion;
  }, []);

  const updateQuestion = useCallback((templateId, questionId, patch) => {
    setTemplates(prev => prev.map(t => {
      if (t.id !== templateId) return t;
      return {
        ...t,
        questionsList: (t.questionsList || []).map(q =>
          q.id === questionId ? { ...q, ...patch } : q
        )
      };
    }));
  }, []);

  const removeQuestion = useCallback((templateId, questionId) => {
    setTemplates(prev => prev.map(t => {
      if (t.id !== templateId) return t;
      const newQuestionsList = (t.questionsList || []).filter(q => q.id !== questionId);
      return {
        ...t,
        questionsList: newQuestionsList,
        questions: newQuestionsList.length
      };
    }));
  }, []);

  const reorderQuestion = useCallback((templateId, fromIndex, toIndex) => {
    setTemplates(prev => prev.map(t => {
      if (t.id !== templateId) return t;
      const questionsList = [...(t.questionsList || [])];
      const [moved] = questionsList.splice(fromIndex, 1);
      questionsList.splice(toIndex, 0, moved);
      return {
        ...t,
        questionsList
      };
    }));
  }, []);

  // Execution operations
  const seedExecutionsFromTemplates = useCallback(() => {
    // This function is kept for API compatibility but actual seeding happens in useEffect
    // Generate some mock executions from templates
    const effectiveTemplates = templates.filter(t => t.status === 'Effective');
    if (effectiveTemplates.length === 0) return;

    const newExecutions = [];
    const statuses = ['Pending', 'In Progress', 'Completed', 'Overdue'];
    const owners = ['Rajesh Kumar', 'Priya Sharma', 'Amit Patel', 'Kavitha Nair', 'Suresh Reddy'];
    
    effectiveTemplates.slice(0, 12).forEach((template, idx) => {
      const status = statuses[idx % statuses.length];
      const passRate = status === 'Completed' ? Math.floor(Math.random() * 20) + 80 : null;
      const execution = {
        id: getNextExecutionId([...executions, ...newExecutions]),
        checklistId: template.id,
        templateName: template.name,
        plant: 'PLANT-A',
        area: template.appliesToArea === 'all' ? 'AREA-OSD' : template.appliesToArea,
        shift: ['Shift 1', 'Shift 2', 'Shift 3'][idx % 3],
        dueDate: new Date(Date.now() + idx * 86400000).toISOString(),
        ownerRole: template.defaultOwnerRole || 'QA_OFFICER',
        executedBy: status === 'Completed' ? owners[idx % owners.length] : null,
        executedAt: status === 'Completed' ? new Date(Date.now() - idx * 3600000).toISOString() : null,
        status,
        score: passRate,
        passRate,
        deviationCreated: status === 'Completed' && passRate < 90 ? `DEV-OSD-2026-${String(100 + idx).padStart(3, '0')}` : null
      };
      newExecutions.push(execution);
    });

    setExecutions(prev => {
      // Only add if we don't have enough executions
      if (prev.length < 8) {
        return [...prev, ...newExecutions];
      }
      return prev;
    });
  }, [templates, executions]);

  // Initialize executions on mount if needed
  useEffect(() => {
    if (executions.length < 8 && templates.length > 0) {
      const effectiveTemplates = templates.filter(t => t.status === 'Effective');
      if (effectiveTemplates.length > 0) {
        // Generate some mock executions from templates
        const newExecutions = [];
        const statuses = ['Pending', 'In Progress', 'Completed', 'Overdue'];
        const owners = ['Rajesh Kumar', 'Priya Sharma', 'Amit Patel', 'Kavitha Nair', 'Suresh Reddy'];
        
        effectiveTemplates.slice(0, 12).forEach((template, idx) => {
          const status = statuses[idx % statuses.length];
          const passRate = status === 'Completed' ? Math.floor(Math.random() * 20) + 80 : null;
          const execution = {
            id: getNextExecutionId([...executions, ...newExecutions]),
            checklistId: template.id,
            templateName: template.name,
            plant: 'PLANT-A',
            area: template.appliesToArea === 'all' ? 'AREA-OSD' : template.appliesToArea,
            shift: ['Shift 1', 'Shift 2', 'Shift 3'][idx % 3],
            dueDate: new Date(Date.now() + idx * 86400000).toISOString(),
            ownerRole: template.defaultOwnerRole || 'QA_OFFICER',
            executedBy: status === 'Completed' ? owners[idx % owners.length] : null,
            executedAt: status === 'Completed' ? new Date(Date.now() - idx * 3600000).toISOString() : null,
            status,
            score: passRate,
            passRate,
            deviationCreated: status === 'Completed' && passRate < 90 ? `DEV-OSD-2026-${String(100 + idx).padStart(3, '0')}` : null
          };
          newExecutions.push(execution);
        });

        setExecutions(prev => [...prev, ...newExecutions]);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const resetDemoData = useCallback(() => {
    localStorage.removeItem('smcp_checklist_templates');
    localStorage.removeItem('smcp_checklist_executions');
    setTemplates(checklistTemplates.map(t => ({
      ...t,
      questions: t.questions || [],
      questionsList: []
    })));
    setExecutions(checklistExecutions);
    toast.success('Demo data reset', { description: 'All checklist data has been reset to defaults' });
  }, []);

  const value = {
    templates,
    executions,
    createTemplate,
    updateTemplate,
    publishTemplate,
    duplicateTemplate,
    retireTemplate,
    addQuestion,
    updateQuestion,
    removeQuestion,
    reorderQuestion,
    seedExecutionsFromTemplates,
    resetDemoData
  };

  return (
    <ChecklistContext.Provider value={value}>
      {children}
    </ChecklistContext.Provider>
  );
};

export const useChecklist = () => {
  const context = useContext(ChecklistContext);
  if (!context) {
    throw new Error('useChecklist must be used within a ChecklistProvider');
  }
  return context;
};

export default ChecklistContext;

